import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Cookie helpers
const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict`;
};

const getCookie = (name: string): string | null => {
  return document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');
};

// Generate or get visitor UUID (with cookie support)
const getVisitorUUID = (): string => {
  const VISITOR_UUID_KEY = 'visitor_uuid';
  
  // Try cookie first (if consent given)
  let visitorUUID = getCookie(VISITOR_UUID_KEY);
  
  // Fallback to localStorage
  if (!visitorUUID) {
    visitorUUID = localStorage.getItem(VISITOR_UUID_KEY);
  }
  
  if (!visitorUUID) {
    // Generate UUID v4
    visitorUUID = crypto.randomUUID();
    localStorage.setItem(VISITOR_UUID_KEY, visitorUUID);
    
    // Also set cookie (will be used if consent is given)
    try {
      setCookie(VISITOR_UUID_KEY, visitorUUID);
    } catch (error) {
      // Cookie might be blocked, that's fine
      console.debug('Cookie setting failed:', error);
    }
  }
  
  return visitorUUID;
};

// Generate session ID (expires after 30 minutes of inactivity)
const getSessionId = (): string => {
  const SESSION_KEY = 'session_id';
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const LAST_ACTIVITY_KEY = 'last_activity';
  
  const now = Date.now();
  const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0');
  
  // Check if session is expired
  if (now - lastActivity > SESSION_TIMEOUT) {
    // Create new session
    const sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sessionId);
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    return sessionId;
  }
  
  // Update last activity
  localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  
  // Return existing session
  return localStorage.getItem(SESSION_KEY) || (() => {
    const sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sessionId);
    return sessionId;
  })();
};

// Track page visit
const trackPageVisit = async (pagePath: string) => {
  try {
    const visitorUUID = getVisitorUUID();
    const sessionId = getSessionId();
    
    await supabase
      .from('page_visits')
      .insert({
        visitor_uuid: visitorUUID,
        page_path: pagePath,
        session_id: sessionId,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      });
  } catch (error) {
    // Silently fail to not disrupt user experience
    console.debug('Traffic tracking error:', error);
  }
};

export const useTrafficTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page visit on route change
    trackPageVisit(location.pathname);
  }, [location.pathname]);
};

export { getVisitorUUID };